import React from "react";
import { Link } from "react-router-dom";
import { Eye } from "lucide-react";
import DataTable from "@/components/Datatable/Datatable";
import { Column } from "@/components/Datatable/types";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Badge } from "@/components/Badge/Badge";
import { BillingSubscription } from "../../api/getBillingSubscriptions/types";
import BillingSummaryCard from "../BillingSummaryCard/BillingSummaryCard";
import { BILLING_STATUS_COLORS } from "../../config/billingStatusColors";
import { useBillingSubscriptionsPage } from "../../hooks/useBillingSubscriptionsPage";
import {
  formatBillingDate,
  formatBillingFrequency,
  formatBillingTrade,
} from "../../utils/billingFormatters";

const columns: Column<BillingSubscription>[] = [
  {
    header: "Product",
    accessorKey: "productName",
    enableSorting: true,
    cell: ({ row }) => row.original.productName,
  },
  {
    header: "Trade",
    accessorKey: "trade",
    enableSorting: true,
    cell: ({ row }) => formatBillingTrade(row.original.trade),
  },
  {
    header: "Frequency",
    accessorKey: "frequency",
    enableSorting: true,
    cell: ({ row }) => formatBillingFrequency(row.original.frequency),
  },
  {
    header: "Start date",
    accessorKey: "startDate",
    enableSorting: true,
    cell: ({ row }) => formatBillingDate(row.original.startDate),
  },
  {
    header: "Last billed",
    accessorKey: "lastInvoice",
    enableSorting: true,
    cell: ({ row }) => {
      const { lastInvoice } = row.original;
      if (!lastInvoice) return "Not billed yet";

      return formatBillingDate(lastInvoice.invoiceDate);
    },
  },
  {
    header: "Status",
    accessorKey: "status",
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex gap-1">
        <Badge
          className={`font-normal ${
            row.original.status === "paused"
              ? BILLING_STATUS_COLORS.yellow
              : BILLING_STATUS_COLORS.green
          }`}
          variant="outline"
        >
          {row.original.status === "paused" ? "Paused" : "Active"}
        </Badge>
        {row.original.paymentStatus === "past_due" && (
          <Badge
            className={`font-normal ${BILLING_STATUS_COLORS.red}`}
            variant="outline"
          >
            Past due
          </Badge>
        )}
      </div>
    ),
  },
  {
    id: "actions",
    header: "",
    cell: ({ row }) => (
      <Link
        aria-label="View invoices"
        className="inline-flex rounded-md p-2 transition-colors hover:bg-gray-100 dark:hover:bg-slate-700"
        title="View invoices"
        to={`/hub/billing/subscriptions/${row.original.id}/invoices`}
      >
        <Eye className="h-4 w-4 text-gray-600 dark:text-gray-300" />
      </Link>
    ),
  },
];

const BillingSubscriptionsPage: React.FC = () => {
  const {
    loading,
    summary,
    sorting,
    handleSortingChange,
    pageRows,
    totalCount,
    pageIndex,
    pageSize,
    handlePageChange,
  } = useBillingSubscriptionsPage();

  return (
    <MainPageWrapper title="Subscriptions">
      {(loading || summary) && (
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <BillingSummaryCard
            detail={summary?.balanceDueDetail}
            label="Balance due"
            loading={loading}
            tooltip="Total unpaid balance across all your invoices."
            value={summary?.balanceDue}
          />
          <BillingSummaryCard
            detail={summary?.activeSubscriptionsDetail}
            label="Active subscriptions"
            loading={loading}
            tooltip="Subscriptions currently enabled on your account, including those with payments paused."
            value={summary?.activeSubscriptions}
          />
        </div>
      )}
      <DataTable<BillingSubscription>
        columns={columns}
        data={pageRows}
        loading={loading}
        noDataMessage="No active subscriptions"
        onPageChange={handlePageChange}
        onSortingChange={handleSortingChange}
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowKeyExtractor={(subscription) => subscription.id}
        sorting={sorting}
        totalCount={totalCount}
      />
    </MainPageWrapper>
  );
};

export default BillingSubscriptionsPage;
